import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { EVENTLIST_URL, getEnvironment, TndResponse } from "@src/pages/home/children/eventlist/EventList.constants";
import { isSessionExpiredRedirectInProgress } from "@src/utils/axiosInterceptor";
import { getSetting } from "@src/utils/storage.utils";

interface TndIdsState {
	data: Array<TndResponse>;
	loading: boolean;
	error: string | null;
}

const initialState: TndIdsState = {
	data: [],
	loading: false,
	error: null,
};

export const fetchTndId = createAsyncThunk<{ data: Array<TndResponse> }, Array<string>>(
	"tndIds/fetchTndIds",
	async (ids, { rejectWithValue }) => {
		const token = getSetting("token", "");
		const environment = getEnvironment();

		const baseUrl = environment === "QA" ? process.env.QA_API_BASE_URL : process.env.PROD_API_BASE_URL;

		if (!baseUrl) {
			return rejectWithValue("API base URL not configured");
		}

		const url = `${baseUrl}/api/events/getMarketplacesEventIds`;

		const tndData = {
			tmEventIds: ids,
			isAlphaNumeric: true,
		};

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: token ? `Bearer ${token}` : "",
			},
			body: JSON.stringify(tndData),
		});

		if (response.status === 401) {
			if (!isSessionExpiredRedirectInProgress) {
				window.dispatchEvent(new CustomEvent("auth:session-expired"));
			}
			return rejectWithValue("Session expired");
		}

		const result = await response.json();
		if (!response.ok) {
			const errorMsg = result?.message || "Failed to fetch Tnd event Id";
			return rejectWithValue(errorMsg);
		}
		const tndEventId = result[0]?.TndEventId;
		if (tndEventId) {
			const url = `${EVENTLIST_URL.TND_URL}tickets/${tndEventId}`;
			window.open(url, "_blank");
		} else {
			window.open(EVENTLIST_URL.TND_URL, "_blank");
		}

		return { data: result as Array<TndResponse> };
	},
);

export const tndSlice = createSlice({
	name: "tndId",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchTndId.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchTndId.fulfilled, (state, action: PayloadAction<{ data: Array<TndResponse> }>) => {
				state.loading = false;
				state.data = action.payload.data;
			})
			.addCase(fetchTndId.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) ?? "Failed to fetch TND IDs";
				state.data = [];
			});
	},
});

export default tndSlice.reducer;
